var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { nanoid } from 'nanoid';
import { RuntimeScheduler } from './modules/scheduler/RuntimeScheduler';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { RuntimeClock } from './modules/clock/RuntimeClock';
import { RuntimePackages } from './modules/RuntimePackages';
import { RuntimeMobx } from './modules/RuntimeMobx';
import { RuntimePermissions } from './modules/RuntimePermissions';
import { RuntimeInfo } from './modules/RuntimeInfo';
import { RuntimeSync } from './modules/RuntimeSync';
import { Devices } from '../device/Devices';
import { RuntimeHostname } from './modules/RuntimeHostname';
import { RuntimeTimeSwitch } from './modules/timeSwitch/RuntimeTimeSwitch';
import { Persistent } from '../api/Persistent';
import { BrowserBot } from '../../packages/EditLazy/classes/bot/BrowserBot';
import { ax, specificAxios } from './axios/axios';
export class Runtime {
    type = 'runtime';
    serverRuntime;
    browserRuntime;
    isServerRuntime = typeof window === 'undefined';
    isBrowserRuntime = typeof window !== 'undefined';
    runtimeInjected;
    registry;
    get id() {
        return nanoid(8);
    }
    get axios() {
        if (this.runtimePermissions.hasEditFunctionality) {
            return specificAxios({ headers: { 'Cache-Control': 'no-cache' } });
        }
        return ax;
    }
    get persistent() {
        return new Persistent(this);
    }
    get runtimeMobx() {
        return new RuntimeMobx(this);
    }
    get runtimePackages() {
        return new RuntimePackages(this);
    }
    get runtimeClock() {
        return new RuntimeClock(this);
    }
    get runtimeTimeSwitch() {
        return new RuntimeTimeSwitch(this);
    }
    get runtimeInfo() {
        return new RuntimeInfo(this);
    }
    get runtimeSync() {
        return new RuntimeSync(this);
    }
    get runtimePermissions() {
        return new RuntimePermissions(this);
    }
    get runtimeScheduler() {
        return new RuntimeScheduler(this);
    }
    get runtimeHostname() {
        return new RuntimeHostname(this);
    }
    get isBot() {
        return !!this.browserRuntime && this.browserRuntime.browserAddressBar.query.bot === '1';
    }
    get runtimeBot() {
        return new BrowserBot(this);
    }
    get devices() {
        return new Devices(this);
    }
    get class() {
        return this.runtimeInjected.entityClass;
    }
    get solidServerRuntime() {
        return this.serverRuntime;
    }
    get solidBrowserRuntime() {
        return this.browserRuntime;
    }
    get model() {
        return new this.class.Model(this);
    }
    get availablePackages() {
        return this.runtimePackages.packageShelf.availablePackages;
    }
    get streamedPackages() {
        return this.runtimePackages.packageShelf.streamedPackages;
    }
    get solidPackages() {
        return this.runtimePackages.packageShelf.solidPackages;
    }
    get fetchPackages() {
        return this.runtimePackages.packageShelf.fetchedPackages;
    }
    get keyboard() {
        return this.devices.keyboard;
    }
    get editChunks() {
        return this.solidPackages.Edit.registryMetaInfo;
    }
    get isDevelopmentStage() {
        if (this.isServerRuntime) {
            return this.solidServerRuntime.envService.isDevelopmentStage;
        }
        return this.solidBrowserRuntime.browserStaging.isDevHostname;
    }
    get isQualityStage() {
        if (this.isServerRuntime) {
            return this.solidServerRuntime.envService.isQualityStage;
        }
        return this.solidBrowserRuntime.browserStaging.isQsHostname;
    }
    get isProductionStage() {
        return !this.isDevelopmentStage && !this.isQualityStage;
    }
    get stagePrefix() {
        if (this.isDevelopmentStage)
            return 'dev';
        if (this.isQualityStage)
            return 'dev';
        return 'www';
    }
    get dbPrefix() {
        if (this.isDevelopmentStage)
            return 'dev_';
        return '';
    }
}
__decorate([
    memoize
], Runtime.prototype, "id", null);
__decorate([
    memoize
], Runtime.prototype, "axios", null);
__decorate([
    memoize
], Runtime.prototype, "persistent", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeMobx", null);
__decorate([
    memoize
], Runtime.prototype, "runtimePackages", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeClock", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeTimeSwitch", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeInfo", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeSync", null);
__decorate([
    memoize
], Runtime.prototype, "runtimePermissions", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeScheduler", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeHostname", null);
__decorate([
    memoize
], Runtime.prototype, "runtimeBot", null);
__decorate([
    memoize
], Runtime.prototype, "devices", null);
__decorate([
    memoize
], Runtime.prototype, "model", null);
export const runtime = new Runtime();
